import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PortalShell, { StatusPill } from "../components/PortalShell";
import { Logo } from "../components/Logo";
import PaymentModal from "../components/PaymentModal";
import ReportView from "../components/ReportView";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { api, fmtErr, DOC_TYPES, RISK_STYLES, SEV_STYLES } from "../api";
import { toast } from "sonner";
import {
  UploadCloud, Trash2, FileText, ScanSearch, Download, IndianRupee, Loader2, AlertTriangle,
  CheckCircle2, Eye, EyeOff, RefreshCw,
} from "lucide-react";

const STEPS = ["documents_pending", "documents_received", "under_verification", "report_locked", "report_ready"];
const STEP_LABELS = {
  documents_pending: "Upload Documents", documents_received: "Documents Received",
  under_verification: "AI Verification", report_locked: "Payment", report_ready: "Report Ready",
};

const FIELD_LABELS = {
  document_type: "Detected Type", document_date: "Document Date", registration_number: "Registration No.",
  registration_date: "Registration Date", survey_number: "Survey No.", hissa_number: "Hissa No.",
  khata_number: "Khata No.", extent: "Extent", extent_unit: "Unit", consideration_amount: "Consideration (₹)",
  property_address: "Property Address", village: "Village", taluk: "Taluk", district: "District", ec_period: "EC Period",
};
const LIST_FIELDS = {
  owner_names: "Owner(s)", seller_names: "Seller / Executant", buyer_names: "Buyer / Claimant",
  encumbrances: "Encumbrances Found", missing_or_illegible_fields: "Missing / Illegible Fields", anomalies: "Anomalies",
};

const AGENT_MESSAGES = [
  "PropLoX AI agent is reading your documents…",
  "Extracting owners, survey numbers & extents…",
  "Cross-verifying Sale Deed ↔ Khata ↔ RTC ↔ EC…",
  "Flagging mismatches with document evidence…",
  "Scoring risk & compiling your report…",
];

export default function VerificationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [docType, setDocType] = useState("sale_deed");
  const [uploading, setUploading] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [agentMsg, setAgentMsg] = useState(0);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/verifications/${id}`);
      setData(d);
    } catch (e) {
      toast.error(fmtErr(e));
      navigate("/dashboard");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data?.status === "under_verification") {
      const t = setInterval(load, 4000);
      return () => clearInterval(t);
    }
  }, [data?.status, load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let done = 0;
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("doc_type", docType);
        await api.post(`/verifications/${id}/documents`, fd);
        done += 1;
      }
      toast.success(done === 1 ? "Document uploaded." : `${done} documents uploaded.`);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeDoc = async (docId) => {
    try {
      await api.delete(`/verifications/${id}/documents/${docId}`);
      toast.success("Document removed.");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  useEffect(() => {
    if (data?.status === "under_verification") {
      const t = setInterval(() => setAgentMsg((m) => (m + 1) % AGENT_MESSAGES.length), 3500);
      return () => clearInterval(t);
    }
  }, [data?.status]);

  const startVerification = () => { setAgentMsg(0); setCountdown(3); };

  useEffect(() => {
    if (countdown === null) return;
    if (countdown < 0) {
      setCountdown(null);
      (async () => {
        try {
          await api.post(`/verifications/${id}/process`);
          toast.success("PropLoX AI agent started — your report takes about a minute.");
          load();
        } catch (e) { toast.error(fmtErr(e)); }
      })();
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), countdown === 0 ? 600 : 900);
    return () => clearTimeout(t);
  }, [countdown, id, load]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/verifications/${id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PropLoX_Title_Scrutiny_${data.report_no || ""}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e?.response?.status === 402) setShowPay(true);
      else toast.error("Could not download the report.");
    }
  };

  if (!data) {
    return <PortalShell title="Loading…"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></PortalShell>;
  }

  const stepIdx = Math.max(0, STEPS.indexOf(data.status === "verification_failed" ? "under_verification" : data.status));
  const report = data.report;
  const paid = data.payment_status === "paid";
  const canUpload = !["under_verification", "report_locked", "report_ready"].includes(data.status);

  return (
    <PortalShell title={data.property.address} subtitle={`Created ${new Date(data.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}`}
      actions={<StatusPill status={data.status} label={data.status_label} />}>
      <PaymentModal open={showPay} onOpenChange={setShowPay} verificationId={id} onSuccess={load} />

      {countdown !== null && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/85 backdrop-blur-sm" data-testid="countdown-overlay">
          <div className="flex flex-col items-center gap-5">
            <Logo boxed height="h-14" />
            <p className="eyebrow text-blue-400">PropLoX AI Agent</p>
            <span key={countdown} className="animate-count-pop font-mono text-8xl font-extrabold tracking-tight text-white" data-testid="countdown-number">
              {countdown === 0 ? "GO!" : countdown}
            </span>
            <p className="text-sm text-slate-300">Warming up document intelligence…</p>
          </div>
        </div>
      )}

      {/* Stepper */}
      <div className="mb-8 flex items-center gap-0 overflow-x-auto rounded-xl border border-slate-200 bg-white p-4" data-testid="status-stepper">
        {STEPS.map((s, i) => (
          <div key={s} className="flex min-w-0 flex-1 items-center">
            <div className="flex flex-col items-center">
              <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold ${
                i < stepIdx ? "border-blue-600 bg-blue-600 text-white" : i === stepIdx ? "border-blue-600 bg-white text-blue-600" : "border-slate-200 bg-white text-slate-300"}`}>
                {i < stepIdx ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              <p className={`mt-1.5 whitespace-nowrap text-[10px] font-semibold ${i <= stepIdx ? "text-slate-900" : "text-slate-400"}`}>{STEP_LABELS[s]}</p>
            </div>
            {i < STEPS.length - 1 && <div className={`mx-2 mb-5 h-0.5 flex-1 ${i < stepIdx ? "bg-blue-600" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      {data.status === "verification_failed" && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4" data-testid="verification-error">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-red-500" />
          <div>
            <p className="text-sm font-semibold text-red-800">Verification could not complete</p>
            <p className="text-xs text-red-600">{data.error || "Some documents could not be read. Try uploading clearer scans."}</p>
            <Button size="sm" variant="outline" className="mt-2" data-testid="retry-verification-button" onClick={startVerification}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Retry
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Documents */}
        <div className="space-y-6 lg:col-span-5">
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="document-upload-card">
            <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900"><UploadCloud className="h-5 w-5 text-blue-600" /> Documents</h3>
            <p className="mt-1 text-xs text-slate-500">Upload Sale Deed, Khata, RTC, Mutation, EC and tax receipts (PDF / JPG / PNG, max 15 MB).</p>
            {canUpload && (
              <div className="mt-4 space-y-3">
                <div className="space-y-1.5">
                  <Label>Document type</Label>
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DOC_TYPES.map((d) => <SelectItem key={d.value} value={d.value} data-testid={`doc-type-${d.value}`}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <label className={`flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 px-4 py-8 text-center transition-colors hover:border-blue-400 ${uploading ? "opacity-60" : ""}`}
                  data-testid="document-upload-dropzone">
                  {uploading ? <Loader2 className="h-6 w-6 animate-spin text-blue-600" /> : <UploadCloud className="h-6 w-6 text-blue-600" />}
                  <span className="text-sm font-medium text-slate-700">{uploading ? "Uploading…" : "Click to upload — one or multiple files"}</span>
                  <input ref={fileRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={upload} disabled={uploading} data-testid="document-file-input" />
                </label>
              </div>
            )}
            <div className="mt-4 space-y-2" data-testid="document-list">
              {(data.documents || []).map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5" data-testid={`doc-row-${d.id}`}>
                  <div className="flex min-w-0 items-center gap-2.5">
                    <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-800">{d.doc_type_label}</p>
                      <p className="truncate text-[11px] text-slate-400">{d.filename}</p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                      d.status === "processed" ? "bg-emerald-50 text-emerald-700" : d.status === "failed" ? "bg-red-50 text-red-600" : d.status === "processing" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"}`}
                      data-testid={`doc-status-${d.id}`}>
                      {d.status === "processing" ? "Reading…" : d.status}
                    </span>
                    {canUpload && (
                      <button onClick={() => removeDoc(d.id)} className="text-slate-300 hover:text-red-500" data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                </div>
              ))}
              {(data.documents || []).length === 0 && <p className="py-4 text-center text-sm text-slate-400">No documents uploaded yet.</p>}
            </div>
            {canUpload && (data.documents || []).length > 0 && (
              <Button className="mt-4 w-full" size="lg" onClick={startVerification} data-testid="start-verification-button">
                <ScanSearch className="mr-2 h-4 w-4" /> Start AI Verification
              </Button>
            )}
            {data.status === "under_verification" && (
              <div className="mt-4 rounded-lg bg-amber-50 px-4 py-3" data-testid="processing-indicator">
                <div className="flex items-center justify-center gap-2 text-sm font-medium text-amber-700">
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                  <span key={agentMsg} className="animate-fade-in">{AGENT_MESSAGES[agentMsg]}</span>
                </div>
                <div className="mt-2.5 h-1 overflow-hidden rounded-full bg-amber-100">
                  <div className="animate-agent-progress h-full w-1/3 rounded-full bg-amber-400" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="space-y-6 lg:col-span-7">
          {report && (
            <>
              <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="risk-card">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="eyebrow">AI Verdict</p>
                    <div className="mt-2 flex items-center gap-3">
                      <span className={`rounded-full px-4 py-1.5 text-sm font-bold ${RISK_STYLES[report.risk?.level] || ""}`} data-testid="risk-badge">{report.risk?.level} Risk</span>
                      <span className="text-sm text-slate-600">{report.suitability}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-xs text-slate-400">{report.report_no}</p>
                    <p className="mt-1 text-xs text-slate-500">{report.findings?.length || 0} finding(s) · {report.documents?.length || 0} document(s)</p>
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  {paid ? (
                    <>
                      <Button onClick={downloadPdf} data-testid="download-report-button"><Download className="mr-2 h-4 w-4" /> Download PDF Report</Button>
                      <Button variant="outline" onClick={() => setShowReport((s) => !s)} data-testid="toggle-report-view-button">
                        {showReport ? <><EyeOff className="mr-2 h-4 w-4" /> Hide Report</> : <><Eye className="mr-2 h-4 w-4" /> View Report</>}
                      </Button>
                    </>
                  ) : (
                    <Button size="lg" onClick={() => setShowPay(true)} data-testid="pay-unlock-button">
                      <IndianRupee className="mr-1 h-4 w-4" /> Pay to Unlock Report
                    </Button>
                  )}
                </div>
              </div>

              {/* Findings */}
              <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="findings-card">
                <h3 className="text-base font-semibold text-slate-900">Cross-Document Flags</h3>
                {(report.findings || []).length === 0 ? (
                  <p className="mt-3 flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" /> All cross-checks passed — no mismatches detected.</p>
                ) : (
                  <div className="mt-4 space-y-3">
                    {report.findings.map((f) => (
                      <div key={f.id} className={`rounded-lg border p-4 ${SEV_STYLES[f.severity] || ""}`} data-testid={`finding-${f.id}`}>
                        <p className="flex items-center gap-2 text-sm font-bold"><AlertTriangle className="h-4 w-4" /> {f.title}
                          <span className="ml-auto rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-bold uppercase">{f.severity}</span></p>
                        <p className="mt-1.5 text-xs leading-relaxed">{f.description}</p>
                        {f.evidence?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {f.evidence.map((ev, i) => (
                              <p key={i} className="rounded bg-white/70 px-2 py-1 font-mono text-[10px]">
                                {ev.document} · {ev.field}: <b>{ev.value}</b>
                              </p>
                            ))}
                          </div>
                        )}
                        <p className="mt-2 text-xs"><b>Action:</b> {f.recommendation}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Extracted data */}
              <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="extraction-card">
                <h3 className="text-base font-semibold text-slate-900">Extracted Data (per document)</h3>
                <Accordion type="single" collapsible className="mt-2">
                  {(data.documents || []).filter((d) => d.extraction).map((d) => (
                    <AccordionItem key={d.id} value={d.id}>
                      <AccordionTrigger data-testid={`extraction-toggle-${d.id}`} className="text-sm font-medium">{d.doc_type_label} <span className="ml-2 font-mono text-[10px] text-slate-400">{d.filename}</span></AccordionTrigger>
                      <AccordionContent>
                        <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-xs sm:grid-cols-2">
                          {Object.entries(FIELD_LABELS).map(([k, label]) => {
                            const v = d.extraction[k];
                            if (!v) return null;
                            return <div key={k} className="flex justify-between gap-3 border-b border-slate-50 py-1"><span className="text-slate-500">{label}</span><span className="text-right font-medium text-slate-800">{String(v)}</span></div>;
                          })}
                          {Object.entries(LIST_FIELDS).map(([k, label]) => {
                            const v = d.extraction[k];
                            if (!Array.isArray(v) || v.length === 0) return null;
                            return <div key={k} className="col-span-full py-1"><span className="text-slate-500">{label}:</span> <span className="font-medium text-slate-800">{v.join(", ")}</span></div>;
                          })}
                          {d.extraction.boundaries && Object.values(d.extraction.boundaries).some(Boolean) && (
                            <div className="col-span-full py-1"><span className="text-slate-500">Boundaries:</span>{" "}
                              <span className="font-medium text-slate-800">
                                {Object.entries(d.extraction.boundaries).filter(([, v]) => v).map(([k, v]) => `${k[0].toUpperCase()}: ${v}`).join(" · ")}
                              </span>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </>
          )}

          {!report && data.status !== "under_verification" && (
            <div className="flex h-full min-h-64 flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="empty-results">
              <ScanSearch className="h-10 w-10 text-slate-300" />
              <p className="mt-4 text-sm font-medium text-slate-700">AI report will appear here</p>
              <p className="mt-1 max-w-sm text-xs leading-relaxed text-slate-500">
                Upload your property documents on the left, then start verification. The PropLoX AI agent will
                extract every field, cross-verify across documents and flag mismatches with evidence.
              </p>
            </div>
          )}
        </div>
      </div>

      {showReport && paid && report && (
        <div className="mt-10">
          <ReportView report={report} />
        </div>
      )}
    </PortalShell>
  );
}
